import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Image,
  Flex,
  VStack,
  HStack,
  Button,
  Spinner,
  Center,
  Tag,
  Icon,
  Link
} from '@chakra-ui/react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { FaDownload, FaArrowLeft } from 'react-icons/fa';
import { getMediaById } from '../api/media';
import { useDownloadWithDisclaimer } from '../components/media/DownloadDisclaimerModal';

const formatFileSize = (bytes) => {
  if (!bytes) return 'N/A';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDimensions = (width, height, unit = 'px') => {
  if (!width || !height) return null;
  if (unit === 'in') return `${width}" x ${height}"`;
  return `${width} x ${height}px`;
};

export const AssetPage = () => {
  const { uuid } = useParams();
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { requestDownload, DisclaimerModal } = useDownloadWithDisclaimer();

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        setLoading(true);
        const data = await getMediaById(uuid);
        setMedia(data);
      } catch (err) {
        setError('Asset not found');
      } finally {
        setLoading(false);
      }
    };
    fetchMedia();
  }, [uuid]);

  if (loading) {
    return (
      <Center py={20}>
        <Spinner size="xl" color="brand.ltRed" thickness="3px" />
      </Center>
    );
  }

  if (error || !media) {
    return (
      <Center py={20} flexDirection="column" gap={4}>
        <Text color="brand.dkGray2" fontSize="lg">{error || 'Asset not found'}</Text>
        <Link as={RouterLink} to="/" color="brand.dkBlue" fontWeight="600">
          Back to Library
        </Link>
      </Center>
    );
  }

  const isVideo = media.mediaType === 'video';
  const isPdf = media.fileType === 'pdf';
  const previewSrc = media.downloadPath || media.filePath;
  const dimensions = formatDimensions(media.width, media.height, media.dimensionUnit);

  return (
    <Box>
      <Box px={{ base: 4, md: 8 }} py={6} maxW="1400px" mx="auto">
        <Link
          as={RouterLink}
          to="/"
          display="inline-flex"
          alignItems="center"
          gap={2}
          color="brand.dkBlue"
          fontWeight="600"
          fontSize="sm"
          mb={6}
          _hover={{ textDecoration: 'underline' }}
        >
          <Icon as={FaArrowLeft} boxSize={3} />
          Back to Library
        </Link>

        <Flex
          direction={{ base: 'column', lg: 'row' }}
          gap={8}
          align="flex-start"
        >
          <Box flex={3} maxW={{ lg: '55%' }}>
            {isVideo ? (
              <video
                src={previewSrc}
                controls
                style={{
                  width: '100%',
                  maxHeight: '70vh',
                  backgroundColor: '#000',
                  borderRadius: '8px'
                }}
              />
            ) : isPdf ? (
              <Box bg="white" borderRadius="md" overflow="hidden" border="1px solid" borderColor="brand.dkGray1">
                <iframe
                  src={previewSrc}
                  title={media.title}
                  style={{
                    width: '100%',
                    height: '70vh',
                    border: 'none'
                  }}
                />
              </Box>
            ) : (
              <Image
                src={previewSrc}
                alt={media.title}
                w="100%"
                maxH="70vh"
                objectFit="contain"
                bg="white"
                borderRadius="md"
                border="1px solid"
                borderColor="brand.dkGray1"
              />
            )}
          </Box>

          <VStack flex={2} align="flex-start" spacing={5} minW={{ lg: '350px' }}>
            <Heading
              as="h1"
              fontSize={{ base: '2xl', md: '3xl' }}
              color="brand.dkBlue"
              lineHeight="1.2"
            >
              {media.title}
            </Heading>

            <VStack align="flex-start" spacing={2} w="100%">
              <HStack spacing={3}>
                <Tag bg="brand.dkBlue" color="white" size="md" fontWeight="600">
                  {media.fileType?.toUpperCase()}
                </Tag>
                <Tag bg="brand.ltGray2" color="brand.dkGray2" size="md">
                  {media.mediaType?.charAt(0).toUpperCase() + media.mediaType?.slice(1)}
                </Tag>
              </HStack>

              <Text fontSize="sm" color="brand.dkGray2">
                Size: {formatFileSize(media.fileSize)}
              </Text>

              {dimensions && (
                <Text fontSize="sm" color="brand.dkGray2">
                  Dimensions: {dimensions}
                </Text>
              )}

              {media.duration && (
                <Text fontSize="sm" color="brand.dkGray2">
                  Duration: {Math.floor(media.duration / 60)}:{String(media.duration % 60).padStart(2, '0')}
                </Text>
              )}
            </VStack>

            {media.topics?.length > 0 && (
              <HStack wrap="wrap" gap={2}>
                {media.topics.map((topic) => (
                  <Tag
                    key={topic.id}
                    bg="brand.ltGray2"
                    color="brand.dkGray2"
                    size="sm"
                    borderRadius="full"
                  >
                    {topic.name}
                  </Tag>
                ))}
              </HStack>
            )}

            <Button
              leftIcon={<FaDownload />}
              bg="brand.ltRed"
              color="white"
              _hover={{ bg: 'brand.dkRed' }}
              size="lg"
              w={{ base: '100%', lg: 'auto' }}
              onClick={() => requestDownload(media.id)}
            >
              Download
            </Button>
          </VStack>
        </Flex>
      </Box>

      <DisclaimerModal />
    </Box>
  );
};
